import { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  PhoneAndroid as PhoneIcon,
  FileDownload as DownloadIcon,
  CheckCircleOutline as CheckIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';

/* ─── i18n ───────────────────────────────────────────────────── */
const i18n = {
  es: {
    badge: 'Encuesta',
    defaultTitle: 'Cuéntanos tu experiencia',
    defaultDesc:
      'Tu opinión nos ayuda a mejorar cada día. Tómate un momento — ¡solo tarda dos minutos!',
    cta: 'Escanea con tu celular y participa',
    download: 'Descargar imagen',
    downloading: 'Descargando…',
    downloaded: '¡Descargado!',
    tooltip: 'Descargar tarjeta como imagen PNG',
    errorLog: 'Error al descargar la imagen:',
    filename: 'encuesta-qr.png',
  },
  en: {
    badge: 'Survey',
    defaultTitle: 'Tell us about your experience',
    defaultDesc:
      'Your feedback helps us improve every day. Take a moment — it only takes two minutes!',
    cta: 'Scan with your phone and participate',
    download: 'Download image',
    downloading: 'Downloading…',
    downloaded: 'Downloaded!',
    tooltip: 'Download card as PNG image',
    errorLog: 'Error downloading image:',
    filename: 'survey-qr.png',
  },
};

function detectLang() {
  const raw = navigator?.language || navigator?.languages?.[0] || 'es';
  return raw.toLowerCase().startsWith('en') ? 'en' : 'es';
}

/* ─── EditableText ───────────────────────────────────────────── */
function EditableText({ value, onChange, sx = {}, component = 'span', align }) {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
    }
  }, [editing]);

  const handleBlur = () => {
    setEditing(false);
    onChange(ref.current?.innerText?.trim() || value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); ref.current?.blur(); }
    if (e.key === 'Escape') { ref.current.innerText = value; ref.current?.blur(); }
  };

  const Tag = component;

  return (
    <Tooltip title="Click to edit" placement="top" arrow>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          justifyContent: align === 'center' ? 'center' : 'flex-start',
          width: align === 'center' ? '100%' : 'auto',
          cursor: 'text',
          borderRadius: '4px',
          outline: editing ? '2px solid rgba(15,110,86,0.35)' : '2px dashed transparent',
          outlineOffset: '2px',
          transition: 'outline 0.15s ease',
          '&:hover': { outline: editing ? undefined : '2px dashed rgba(15,110,86,0.25)' },
        }}
        onClick={() => setEditing(true)}
      >
        <Tag
          ref={ref}
          contentEditable={editing}
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{
            outline: 'none',
            cursor: editing ? 'text' : 'pointer',
            minWidth: 4,
            textAlign: align,
            ...sx,
          }}
        >
          {value}
        </Tag>

        {/* Info icon — visible on hover, hidden while editing */}
        <InfoIcon
          aria-hidden="true"
          sx={{
            fontSize: 12,
            color: 'rgba(15,110,86,0.55)',
            flexShrink: 0,
            opacity: hovered && !editing ? 1 : 0,
            transform: hovered && !editing ? 'scale(1)' : 'scale(0.7)',
            transition: 'opacity 0.15s ease, transform 0.15s ease',
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Tooltip>
  );
}

/* ─── Component ──────────────────────────────────────────────── */
export default function SurveyQR({
  url = 'https://encuesta.ejemplo.com',
  title,
  description,
  accentColor = '#0F6E56',
}) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const lang = detectLang();
  const t = i18n[lang];

  const [badgeText, setBadgeText] = useState(t.badge);
  const [ctaText, setCtaText] = useState(t.cta);

  const resolvedTitle = title ?? t.defaultTitle;
  const resolvedDesc = description ?? t.defaultDesc;
  const shortUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const link = document.createElement('a');
      link.download = t.filename;
      link.href = canvas.toDataURL('image/png');
      link.click();

      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2500);
    } catch (err) {
      console.error(t.errorLog, err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        p: 2,
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      {/* ── Card (captured area) ── */}
      <Card
        ref={cardRef}
        elevation={0}
        sx={{
          width: 320,
          borderRadius: '20px',
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        {/* Top accent bar */}
        <Box sx={{ height: 5, width: '100%', background: accentColor }} />

        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 3.5,
            pt: 3.5,
            pb: 3,
            gap: 0,
            '&:last-child': { pb: 3 },
          }}
        >
          {/* Badge — editable */}
          <Box
            sx={{
              mb: 1.75,
              height: 26,
              px: 1.5,
              display: 'inline-flex',
              alignItems: 'center',
              background: `${accentColor}18`,
              borderRadius: '100px',
              cursor: 'text',
            }}
          >
            <EditableText
              value={badgeText}
              onChange={setBadgeText}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: accentColor,
                fontFamily: 'inherit',
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            component="h2"
            align="center"
            sx={{
              fontFamily: "'Playfair Display', 'Georgia', serif",
              fontSize: 22,
              fontWeight: 700,
              color: '#111',
              lineHeight: 1.3,
              mb: 1.5,
            }}
          >
            {resolvedTitle}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            align="center"
            sx={{
              fontSize: 13.5,
              color: '#555',
              lineHeight: 1.65,
              mb: 3,
            }}
          >
            {resolvedDesc}
          </Typography>

          {/* QR frame */}
          <Box sx={{ position: 'relative', p: 1.5, mb: 2 }}>
            <Corner pos="tl" color={accentColor} />
            <Corner pos="tr" color={accentColor} />
            <Corner pos="bl" color={accentColor} />
            <Corner pos="br" color={accentColor} />

            <QRCodeSVG
              value={url}
              size={168}
              bgColor="transparent"
              fgColor="#1a1a1a"
              level="M"
              style={{ display: 'block' }}
            />
          </Box>

          {/* Short URL */}
          <Typography
            sx={{
              fontFamily: "'DM Mono', 'Courier New', monospace",
              fontSize: 11,
              color: '#888',
              mb: 2.5,
              letterSpacing: '0.02em',
              wordBreak: 'break-all',
              textAlign: 'center',
            }}
          >
            {shortUrl}
          </Typography>

          {/* CTA — editable */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PhoneIcon sx={{ fontSize: 15, color: accentColor, mt: '1px', flexShrink: 0 }} />
            <EditableText
              value={ctaText}
              onChange={setCtaText}
              sx={{
                fontSize: 12.5,
                fontWeight: 500,
                color: accentColor,
                fontFamily: 'inherit',
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* ── Download button (outside captured area) ── */}
      <Tooltip title={t.tooltip} placement="bottom">
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={downloading}
          startIcon={
            downloading ? (
              <CircularProgress size={16} sx={{ color: 'inherit' }} />
            ) : downloaded ? (
              <CheckIcon sx={{ fontSize: 18 }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 18 }} />
            )
          }
          sx={{
            background: downloaded ? '#2e7d32' : accentColor,
            color: '#fff',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13.5,
            px: 3,
            py: 1.1,
            boxShadow: `0 4px 14px ${accentColor}40`,
            '&:hover': {
              background: accentColor,
              filter: 'brightness(1.1)',
              boxShadow: `0 6px 18px ${accentColor}55`,
            },
            '&:disabled': {
              background: accentColor,
              color: '#fff',
              opacity: 0.75,
            },
            transition: 'all 0.2s ease',
          }}
        >
          {downloading ? t.downloading : downloaded ? t.downloaded : t.download}
        </Button>
      </Tooltip>
    </Box>
  );
}

/* ─── Corner bracket decoration ─────────────────────────────── */
function Corner({ pos, color }) {
  const size = 14;
  const offset = -1;

  const posStyles = {
    tl: { top: offset, left: offset, borderTop: `2px solid ${color}`, borderLeft: `2px solid ${color}` },
    tr: { top: offset, right: offset, borderTop: `2px solid ${color}`, borderRight: `2px solid ${color}` },
    bl: { bottom: offset, left: offset, borderBottom: `2px solid ${color}`, borderLeft: `2px solid ${color}` },
    br: { bottom: offset, right: offset, borderBottom: `2px solid ${color}`, borderRight: `2px solid ${color}` },
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        ...posStyles[pos],
      }}
    />
  );
}